import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Link } from 'react-router';
import { routes } from '../../../constants/routes';
import theme from '../../../theme';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../utils/redux';
import {
  setAccountInfo,
  setCurrentStep,
} from '../../../utils/redux/slices/signupSlice';
import { createAccountSchema } from '../../../schemas';
import { debounce } from 'lodash';
import { checkEmailApi } from '../../../utils/apis';

export default function CreateAccount() {
  const dispatch = useDispatch();
  const { accountInfo } = useSelector((state: RootState) => state.signup);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const togglePasswordVisibility = () => setIsPasswordVisible(prev => !prev);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheckEmail = useCallback(
    debounce(async (email: string) => {
      if (email && email.includes('@') && email.includes('.')) {
        setIsCheckingEmail(true);
        try {
          const response = await checkEmailApi(email);
          setEmailExists(response.data.exists);
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setIsCheckingEmail(false);
        }
      }
    }, 500),
    [] // No dependencies, debounce handles the timing logic
  );

  // Clean up the debounce function on unmount
  useEffect(() => {
    return () => {
      debouncedCheckEmail.cancel();
    };
  }, [debouncedCheckEmail]);

  const formik = useFormik({
    initialValues: {
      email: accountInfo.email || '',
      password: accountInfo.password || '',
      terms: accountInfo.terms || false,
      accountType: accountInfo.accountType || 'trial',
    },
    validationSchema: createAccountSchema,
    onSubmit: values => {
      // Save form data to Redux store
      dispatch(
        setAccountInfo({
          email: values.email,
          password: values.password,
          terms: values.terms,
          accountType: values.accountType,
        })
      );

      // Move to next step
      dispatch(setCurrentStep(2));
    },
  });

  // Handle email change to check if it exists
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.handleChange(e);

    if (value) {
      debouncedCheckEmail(value);
    } else {
      setEmailExists(false);
    }
  };

  // Check if form is valid whenever values or errors change
  useEffect(() => {
    const { email, password, terms, accountType } = formik.values;
    const hasValues =
      email.trim() !== '' &&
      password.trim() !== '' &&
      terms === true &&
      (accountType === 'trial' || accountType === 'prod');
    const hasNoErrors = Object.keys(formik.errors).length === 0;
    const isEmailValid = !emailExists;

    setIsFormValid(hasValues && hasNoErrors && isEmailValid);
  }, [formik.values, formik.errors, emailExists]);

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        flex: '1 1 50%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2, md: 2, lg: 2 },
        bgcolor: 'white',
        overflow: 'auto',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            px: '24px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: '#2B2D3F',
              fontSize: '24px',
            }}
          >
            Create Your Account
          </Typography>
          <Typography
            sx={{
              color: '#6B6F80',
              fontSize: '14px',
            }}
          >
            Sign up now and bring your AI assistant to life
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mb: 2, px: '24px' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Email ID
            </Typography>
            <TextField
              name="email"
              placeholder="abc@example.com"
              size="small"
              value={formik.values.email}
              onChange={handleEmailChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.email && Boolean(formik.errors.email)) ||
                emailExists
              }
              helperText={
                (formik.touched.email && formik.errors.email) ||
                (emailExists ? 'Email is already registered' : '') ||
                (isCheckingEmail ? 'Checking email...' : '')
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  width: '450px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2, px: '24px' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Password
            </Typography>
            <TextField
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder="Password"
              size="small"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={togglePasswordVisibility}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    {isPasswordVisible ? (
                      <EyeSlash size={16} color="gray" />
                    ) : (
                      <Eye size={16} color="gray" />
                    )}
                  </Button>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  width: '450px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2, px: '24px' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Account type
            </Typography>
            <FormControl
              size="small"
              error={
                formik.touched.accountType && Boolean(formik.errors.accountType)
              }
              sx={{
                width: '450px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              <Select
                id="accountType"
                name="accountType"
                value={formik.values.accountType}
                inputProps={{ 'aria-label': 'Account type' }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="prod">Production</MenuItem>
              </Select>
              {formik.touched.accountType && formik.errors.accountType ? (
                <FormHelperText>{formik.errors.accountType}</FormHelperText>
              ) : null}
            </FormControl>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                name="terms"
                checked={formik.values.terms}
                onChange={formik.handleChange}
                size="small"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: '16px',
                  },
                  pl: '34px',
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                  '&.Mui-checked': {
                    bgcolor: 'transparent',
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ color: '#6B6F80', fontSize: '0.875rem' }}
              >
                I agree to Quibble AI's{' '}
                <Link
                  to="/terms"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                  }}
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                  }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid}
            sx={{
              py: 1.5,
              mx: '24px',
              bgcolor: theme.palette.primary.main,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: 'none',
              mb: 2,
              fontSize: '14px',
              height: '34px',
              width: '450px',
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9E9E9E',
              },
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                boxShadow: 'none',
                scale: '1.02',
              },
            }}
          >
            Get Started
          </Button>

          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: '#6B6F80',
            }}
          >
            Already have an account?{' '}
            <Link
              to={routes.login}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
              }}
            >
              Sign In
            </Link>
          </Typography>
        </form>
      </Container>
    </Box>
  );
}
